import { useState, useEffect, useRef } from 'react';
import { ref, set, onValue, onDisconnect, serverTimestamp, Unsubscribe } from 'firebase/database';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { rtdb } from '../providers/firebase/firebase';

/** Reverse RTDB key sanitization: -plus- → +, -at- → @, -dot- → . */
export function unsanitizeKey(key: string): string {
    return key.replace(/-plus-/g, '+').replace(/-at-/g, '@').replace(/-dot-/g, '.');
}

/** Unsanitize all keys in a Record */
function unsanitizeKeys<T>(data: Record<string, T>): Record<string, T> {
    const result: Record<string, T> = {};
    for (const [key, value] of Object.entries(data)) {
        result[unsanitizeKey(key)] = value;
    }
    return result;
}

/**
 * Hook that manages RTDB presence and real-time data subscription for a resource.
 *
 * - Waits for Firebase Auth to be ready before subscribing
 * - Sets presence at `presence/{resource}/{uid}` on mount (unless skipPresence is set)
 * - Subscribes to `{resource}/{uid}` for live data
 * - Unsanitizes RTDB keys to restore original account IDs
 * - Cleans up presence and listener on unmount
 *
 * Returns null data and connected=false when uid is undefined.
 */
export function useRealtimeStream<T>(
    resource: string,
    uid: string | undefined,
    options?: { skipPresence?: boolean }
): { data: Record<string, T> | null; connected: boolean } {
    const [data, setData] = useState<Record<string, T> | null>(null);
    const [connected, setConnected] = useState(false);
    const unsubAuthRef = useRef<(() => void) | null>(null);
    const unsubDataRef = useRef<Unsubscribe | null>(null);

    useEffect(() => {
        if (!uid) return;

        const skipPresence = options?.skipPresence ?? false;
        let cancelled = false;
        const auth = getAuth();

        function subscribe() {
            if (cancelled) return;

            const dataRef = ref(rtdb, `${resource}/${uid}`);

            if (!skipPresence) {
                const presenceRef = ref(rtdb, `presence/${resource}/${uid}`);
                set(presenceRef, {
                    online: true,
                    connectedAt: serverTimestamp(),
                });
                onDisconnect(presenceRef).remove();
            }

            // Subscribe to data — mark connected on first successful snapshot.
            // Track the last JSON to skip re-delivers of identical data
            // (e.g. Firebase reconnects on browser focus change).
            let firstSnapshot = true;
            let prevJson: string | null = null;
            unsubDataRef.current = onValue(
                dataRef,
                (snapshot) => {
                    if (firstSnapshot) {
                        setConnected(true);
                        firstSnapshot = false;
                    }
                    const val = snapshot.val();
                    if (val) {
                        const json = JSON.stringify(val);
                        if (json === prevJson) return;
                        prevJson = json;
                        setData(unsanitizeKeys<T>(val));
                    } else if (prevJson !== null) {
                        prevJson = null;
                        setData(null);
                    }
                },
                (error) => {
                    console.error(`RTDB error for ${resource}:`, error);
                    setConnected(false);
                }
            );
        }

        // If auth user is already available, subscribe immediately
        if (auth.currentUser) {
            subscribe();
        } else {
            // Wait for auth state to be ready before subscribing
            const unsubAuth = onAuthStateChanged(auth, (user) => {
                if (user && !cancelled) {
                    unsubAuth();
                    unsubAuthRef.current = null;
                    subscribe();
                }
            });
            unsubAuthRef.current = unsubAuth;
        }

        // Cleanup
        return () => {
            cancelled = true;
            unsubAuthRef.current?.();
            unsubAuthRef.current = null;
            unsubDataRef.current?.();
            unsubDataRef.current = null;
            if (!skipPresence) {
                const presenceRef = ref(rtdb, `presence/${resource}/${uid}`);
                set(presenceRef, null);
            }
            setConnected(false);
            setData(null);
        };
    }, [resource, uid, options?.skipPresence]);

    return { data, connected };
}
